import { api, LightningElement } from "lwc";
import debounce from "lodash/debounce";

import { getFocusableElements } from "common/a11y";
import { attrToBool } from "common/helpers";

// This is a re-implementation of https://github.com/andreasbm/focus-trap
// since LWC does not currently support custom elements in a template
export default class extends LightningElement {
  private active = true;
  private refocusOnDisconnect = true;

  @api
  get inactive() {
    return !this.active;
  }

  // Changes here must also manage the active element
  set inactive(inactive: boolean) {
    this.refocusOnDisconnect = false;

    this.active = !attrToBool(inactive);

    if (this.active) {
      this.$activeElement = getActiveElement(document.activeElement);
      this.focusFirstElement();
    } else {
      // "inactive" is effectivly the same as disconnected
      this.refocusActiveElement();
    }
  }

  private focused = false;
  private init = false;

  private $start!: HTMLElement;
  private $end!: HTMLElement;

  private $activeElement: Element | null = null;

  private get startTabIndex() {
    return !this.focused || this.inactive ? "-1" : "0";
  }

  private get endTabIndex() {
    return !this.focused || this.inactive ? "-1" : "0";
  }

  @api focusFirst() {
    requestAnimationFrame(() => this.focusFirstElement());
  }

  renderedCallback() {
    if (this.init) return;
    else this.init = true;

    this.$start = this.template.querySelector(
      `[data-id="start"]`
    ) as HTMLElement;
    this.$end = this.template.querySelector(`[data-id="end"]`) as HTMLElement;

    this.$start.addEventListener("focus", this.focusLastElement);
    this.$end.addEventListener("focus", this.focusFirstElement);

    this.addEventListener("focusin", this.onFocusIn);
    this.addEventListener("focusout", this.onFocusOut);
    this.addEventListener("keyup", this.onKeyUp as EventListener);

    this.$activeElement = getActiveElement(document.activeElement);

    this.focusFirst();
  }

  disconnectedCallback() {
    this.removeEventListener("focusin", this.onFocusIn);
    this.removeEventListener("focusout", this.onFocusOut);
    if (this.refocusOnDisconnect) {
      this.refocusActiveElement();
    }
  }

  private refocusActiveElement() {
    if (this.$activeElement && this.$activeElement instanceof HTMLElement) {
      this.$activeElement.focus();
    }
  }

  private focusFirstElement = () => {
    this.trapFocus();
  };

  private focusLastElement = () => {
    this.trapFocus(true);
  };

  private onFocusIn = debounce(() => {
    this.focused = true;
  }, 0);

  private onFocusOut = debounce(() => {
    this.focused = false;
  }, 0);

  private onKeyUp = (e: KeyboardEvent) => {
    if (e.key === "Esc" || e.key === "Escape") {
      this.dispatchEvent(new CustomEvent("requestclose"));
    }
  };

  private getFocusableElements(): HTMLElement[] {
    let slot = this.template.querySelector("slot") as HTMLSlotElement | null;
    // Component might not be rendered yet
    if (!slot) return [];
    return getFocusableElements(slot);
  }

  private trapFocus(trapToEnd?: boolean) {
    if (this.inactive) return;
    let focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      if (trapToEnd) {
        focusableElements[focusableElements.length - 1].focus();
      } else {
        focusableElements[0].focus();
      }
    }
  }
}

export function getActiveElement(element: Element | null): Element | null {
  if (element && element.shadowRoot)
    return getActiveElement(element.shadowRoot.activeElement);
  return element;
}
